import {
  Box,
  CircularProgress,
  Grid,
  LinearProgress,
  Typography,
  linearProgressClasses,
  styled,
} from '@mui/material';
import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { ReactComponent as WarningIcon } from '../../assets/pulse-warning-icon.svg';
import { customRenderers } from '../../helpers';
import {
  QuantDiagnosticColors,
  getQuantChartColor,
  getQuantScoreSubText,
} from '../../modules/automated-insights/constants';
import HalfDoughnutScoreGraph from '../reports/components/HalfDoughnutScoreGraph';
import { ExploreButton } from './ExploreButton';

const LinearScoreBar = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'barColor' && prop !== 'barBackgroundColor',
})(({ theme, barColor, barBackgroundColor }) => ({
  height: 16,
  width: '100%',
  borderRadius: '10px',
  backgroundColor: barBackgroundColor,
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: '20px',
    backgroundColor: barColor,
  },
}));

const WarningMessage = () => {
  return (
    <Box
      p={2}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, borderRadius: '10px', backgroundColor: '#F8F8F8' }}>
      <WarningIcon style={{ width: '20px' }} />
      <Typography fontSize={14}>Due to limited content in this study, scores may not be accurate.</Typography>
    </Box>
  );
};

const QuantScore = ({ score = -1 }) => {
  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '12px',
        padding: {
          xs: 3.5,
          lg: 2,
        },

        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.secondary,
      }}>
      <Box>
        <Typography fontSize={16} fontWeight={700} textAlign="center">
          Experience Quality Score
        </Typography>
      </Box>
      <Box sx={{ width: 'inherit', marginTop: -4, paddingX: 2, paddingBottom: 3 }}>
        <HalfDoughnutScoreGraph
          score={score}
          backgroundColor={getQuantChartColor(score)?.primary}
          secondaryBackgroundColor={getQuantChartColor(score)?.secondary}
          hoverBackgroundColor={getQuantChartColor(score)?.primary}
          textFont={'Montserrat'}
          textFontSize={1.8}
          textFontWeight={700}
          showBorders={true}
          showScale={false}
        />
        <Typography sx={{ marginTop: -3, fontSize: 12, textAlign: 'center' }}>
          {score >= 0 ? getQuantScoreSubText(score) : ''}
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: 14,
          right: 14,
          paddingX: '4px',
          borderRadius: '4px',
          border: '1px solid rgba(161, 161, 161, 0.23)',
          backgroundColor: 'rgba(224, 230, 234, 0.08)',
        }}>
        <Typography sx={{ fontSize: '11px', textTransform: 'uppercase' }}>beta</Typography>
      </Box>
    </Box>
  );
};

const DiagnosticScore = ({ name, score, scoreColor, scoreBarColor, scoreBarBgColor }) => {
  const roundedScore = useMemo(() => Math.round(score), [score]);

  return (
    <Box>
      <Typography fontSize={14} fontWeight={700} sx={{ marginBottom: 1.5 }}>
        {name}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}>
        <LinearScoreBar
          variant="determinate"
          barColor={scoreBarColor}
          barBackgroundColor={scoreBarBgColor}
          value={roundedScore}
        />
        <Typography fontSize={14} fontWeight={700} sx={{ paddingLeft: 2, color: scoreColor }}>
          {roundedScore}
        </Typography>
      </Box>
    </Box>
  );
};

const Quant = ({
  quantData,
  onQuestionsClick,
  setSelectedTab,
  setSelectedQuantTheme,
  hideReportContent,
  isSessionOwner,
}) => {
  const showLoadingIndicatorForShareLink = !isSessionOwner && hideReportContent;

  return showLoadingIndicatorForShareLink ? (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress color="secondary" />
    </Box>
  ) : (
    <Box
      className="lightContainer"
      sx={{ height: '100%', paddingBottom: 6, overflow: hideReportContent ? 'hidden' : 'auto' }}>
      {quantData?.isFlagged && (
        <Box px={2}>
          <WarningMessage />
        </Box>
      )}
      <Grid
        container
        rowGap={2}
        columnGap={2}
        sx={{
          width: '100%',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'center', lg: 'initial' },
          paddingX: 2,
          paddingTop: 2,
          paddingBottom: 3,
        }}>
        <Grid
          item
          sx={{
            width: { xs: '100%', lg: '30%' },
            minWidth: { lg: 254 },
            maxWidth: { xs: 270, lg: 300 },
          }}>
          <QuantScore score={quantData?.average} />
        </Grid>
        <Grid item xs>
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              fontSize: '14px',
            }}>
            <ReactMarkdown components={customRenderers}>{quantData?.summary}</ReactMarkdown>
          </Box>
        </Grid>
      </Grid>
      <Grid container>
        {quantData?.scores?.map((item, index) => (
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            key={item?.name}
            sx={{
              borderTop: '1px solid #ECECEC',
              borderBottom: '1px solid #ECECEC',
              borderRight: {
                xs: 'none',
                md: '1px solid #ECECEC',
                lg: index < quantData?.scores?.length - 1 ? '1px solid #ECECEC' : 'none',
              },
            }}>
            <Grid container flexDirection="column" sx={{ height: '100%', paddingTop: 3.5 }}>
              <Grid
                item
                sx={{ width: '100%', maxWidth: { sm: 390, md: 'none' }, paddingX: 2, paddingBottom: 2 }}>
                <DiagnosticScore
                  name={item?.name}
                  score={item?.score}
                  scoreColor={QuantDiagnosticColors[item?.name]?.primary}
                  scoreBarColor={QuantDiagnosticColors[item?.name]?.primary}
                  scoreBarBgColor={QuantDiagnosticColors[item?.name]?.secondary}
                />
              </Grid>
              <Grid
                item
                sx={{
                  flexGrow: 1,
                  paddingX: 2,
                  paddingTop: 2,
                  paddingBottom: 1.5,
                  backgroundColor: QuantDiagnosticColors[item?.name]?.background,
                }}>
                <Grid container flexDirection="column" justifyContent="space-between" sx={{ height: '100%' }}>
                  <Grid item>
                    {item?.positiveReason && item?.negativeReason ? (
                      <Box>
                        <Typography fontSize={13} fontWeight={600}>
                          Positive
                        </Typography>
                        <Typography fontSize={12}>{item?.positiveReason}</Typography>
                        <Typography fontSize={13} fontWeight={600} pt={1}>
                          Negative
                        </Typography>
                        <Typography fontSize={12}>{item?.negativeReason}</Typography>
                      </Box>
                    ) : (
                      <Typography fontSize={12}>{item?.reason}</Typography>
                    )}
                  </Grid>
                  <Grid item sx={{ marginTop: 2, alignSelf: 'flex-end' }}>
                    <ExploreButton
                      quantCategory={item?.name}
                      setSelectedQuantTheme={setSelectedQuantTheme}
                      setSelectedTab={setSelectedTab}
                      onQuestionsClick={onQuestionsClick}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Quant;
